import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Textarea } from "@/shared/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Building2 } from "lucide-react";
import { PROPERTY_TYPES, PropertyFormData } from "./types";

interface BasicInfoTabProps {
  formData: PropertyFormData;
  updateFormData: (field: keyof PropertyFormData, value: string) => void;
}

export function BasicInfoTab({ formData, updateFormData }: BasicInfoTabProps) {
  const availableSubTypes = formData.property_type
    ? PROPERTY_TYPES[formData.property_type as keyof typeof PROPERTY_TYPES] || []
    : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-4 w-4" />
          Basic Property Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Property Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => updateFormData('name', e.target.value)}
              placeholder="e.g., Downtown Office Building"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="property_type">Property Type *</Label>
            <Select
              value={formData.property_type}
              onValueChange={(value) => updateFormData('property_type', value)}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select property type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Commercial">Commercial</SelectItem>
                <SelectItem value="Residential">Residential</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {formData.property_type && (
          <div className="space-y-2">
            <Label htmlFor="property_sub_type">Property Sub-Type</Label>
            <Select
              value={formData.property_sub_type}
              onValueChange={(value) => updateFormData('property_sub_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sub-type (optional)" />
              </SelectTrigger>
              <SelectContent>
                {availableSubTypes.map((subType) => (
                  <SelectItem key={subType} value={subType}>{subType}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="property_description">Description</Label>
          <Textarea
            id="property_description"
            value={formData.property_description}
            onChange={(e) => updateFormData('property_description', e.target.value)}
            placeholder="Describe the property, its features, and notable characteristics..."
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  );
}
